import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useReactiveVar } from "@apollo/client";
import { userVar, errorsVar, successErrorType, duelsInfoVar } from "../../cache/cache";
import {
    DuelsInfo,
    DuelsInfoLangText,
    DuelsInfoLangTextInput,
    UpdateDuelsInfoMutationVariables,
    useUpdateDuelsInfoMutation,
} from "../../types/graphql";
import { setError } from "../../misc/common";
import { DUELS_INFO } from "../../graphql/queries";
import { useTranslation } from "react-i18next";
import { getCurrentLanguage, SUPPORTED_LANGUAGES, SupportedLanguage } from "../../i18n";
import { ReactComponent as TickCircleIcon } from "../../img/tick-circle.svg";

import "./styles.css";

const Textarea = React.lazy(() => import("../../components/common/Textarea"));
const Button = React.lazy(() => import("../../components/common/Button"));
const ErrorMessage = React.lazy(() => import("../../components/common/ErrorMessage"));

const getLangTextInput = (value?: DuelsInfoLangText | null): DuelsInfoLangTextInput => ({
    ru: value?.ru || "",
    en: value?.en || "",
    et: value?.et || "",
    es: value?.es || "",
});

const getInitialFields = (duelsInfo?: DuelsInfo | null): UpdateDuelsInfoMutationVariables => ({
    id: duelsInfo?.id || "",
    text1: getLangTextInput(duelsInfo?.text1),
    text2: getLangTextInput(duelsInfo?.text2),
    text3: getLangTextInput(duelsInfo?.text3),
    text4: getLangTextInput(duelsInfo?.text4),
});

const UpdateDuelsInfo: React.FC = () => {
    const { t } = useTranslation();
    const duelsInfo = useReactiveVar(duelsInfoVar);
    const [fields, setFields] = React.useState<UpdateDuelsInfoMutationVariables>(getInitialFields(duelsInfo));
    const [activeLanguage, setActiveLanguage] = React.useState<SupportedLanguage>(getCurrentLanguage());

    const navigate = useNavigate();
    const location = useLocation();
    const user = useReactiveVar(userVar);
    const isAdmin = user?.roles?.map((r) => r.id)?.includes("admin");
    let errors = useReactiveVar(errorsVar);

    const [updateDuelsInfo] = useUpdateDuelsInfoMutation({
        onCompleted: (data) => {
            if (data?.updateDuelsInfo) {
                navigate(location.pathname + location.search);
            }
        },
        refetchQueries: () => {
            return [
                {
                    query: DUELS_INFO,
                },
            ];
        },
        onError: (e) => {
            console.log(e);
            const newErrors: successErrorType[] = [];
            newErrors.push({ place: "update-duelsInfo.system", message: e?.message });
            if (newErrors.length > 0) {
                errorsVar(setError(errors, newErrors));
            }
        },
    });

    React.useEffect(() => {
        if (!user || !isAdmin) {
            navigate("/", { replace: true });
        }
    }, [user, navigate, isAdmin]);

    React.useEffect(() => {
        setFields(getInitialFields(duelsInfo));
    }, [duelsInfo]);

    const updateFieldValue = (field: "text1" | "text2" | "text3" | "text4", value: string) => {
        setFields((prev) => ({
            ...prev,
            [field]: {
                ...prev[field],
                [activeLanguage]: value,
            },
        }));
    };

    const isLanguageCompleted = (lang: SupportedLanguage): boolean => {
        return [fields.text1[lang], fields.text2[lang], fields.text3[lang], fields.text4[lang]].every(
            (value) => value.trim().length > 0,
        );
    };

    const submit = () => {
        errors = errorsVar([]);

        const newErrors: successErrorType[] = [];

        if (newErrors.length === 0) {
            updateDuelsInfo({ variables: fields });
        } else {
            errorsVar(setError(errors, newErrors));
        }
    };
    const allErrors = errors.filter((el) => ["update-duelsInfo.system"].includes(el?.place || ""));

    return (
        <div className="update-duelsInfo">
            <div className="update-duelsInfo__title">{t("popup.updateDuelsInfo.title")}</div>
            {allErrors.length > 0 && (
                <ErrorMessage
                    className="update-duelsInfo__error"
                    errors={allErrors}
                />
            )}
            <>
                <div className="update-duelsInfo__language-tabs">
                    {SUPPORTED_LANGUAGES.map((lang) => (
                        <button
                            key={lang}
                            type="button"
                            className={`update-duelsInfo__language-tab ${activeLanguage === lang ? "active" : ""}`}
                            onClick={() => setActiveLanguage(lang)}
                        >
                            <span className="update-duelsInfo__language-tab-content">
                                {isLanguageCompleted(lang) && (
                                    <TickCircleIcon className="update-duelsInfo__language-tab-icon" />
                                )}
                                {t(`common.languages.${lang}`)}
                            </span>
                        </button>
                    ))}
                </div>
                <Textarea
                    id="update-duelsInfo-textarea"
                    className="update-duelsInfo__textarea"
                    title={t("popup.updateDuelsInfo.fields.topBold")}
                    placeholder={t("popup.updateDuelsInfo.fields.placeholder")}
                    value={fields.text1[activeLanguage]}
                    onChange={(val) => updateFieldValue("text1", val)}
                />
                <Textarea
                    id="update-duelsInfo-textarea"
                    className="update-duelsInfo__textarea"
                    title={t("popup.updateDuelsInfo.fields.topRegular")}
                    placeholder={t("popup.updateDuelsInfo.fields.placeholder")}
                    value={fields.text2[activeLanguage]}
                    onChange={(val) => updateFieldValue("text2", val)}
                />
                <Textarea
                    id="update-duelsInfo-textarea"
                    className="update-duelsInfo__textarea"
                    title={t("popup.updateDuelsInfo.fields.bottomBold")}
                    placeholder={t("popup.updateDuelsInfo.fields.placeholder")}
                    value={fields.text3[activeLanguage]}
                    onChange={(val) => updateFieldValue("text3", val)}
                />
                <Textarea
                    id="update-duelsInfo-textarea"
                    className="update-duelsInfo__textarea"
                    title={t("popup.updateDuelsInfo.fields.bottomRegular")}
                    placeholder={t("popup.updateDuelsInfo.fields.placeholder")}
                    value={fields.text4[activeLanguage]}
                    onChange={(val) => updateFieldValue("text4", val)}
                />
            </>
            <Button
                text={t("popup.updateDuelsInfo.submit")}
                mode="burgundy"
                onClick={() => submit()}
            />
        </div>
    );
};

export default UpdateDuelsInfo;
